import { LeagueRole } from "../types/models";

export const LEAGUE_ROLES: LeagueRole[] = ["owner", "admin", "scorekeeper"];

export const LEAGUE_ROLE_LABELS: Record<LeagueRole, string> = {
  owner: "Owner",
  admin: "Admin",
  scorekeeper: "Scorekeeper",
};

/**
 * Whether a member with `actorRole` may add, edit, or remove a membership
 * holding `role`. Mirrors LeagueUserPolicy on the API (which remains the
 * authority): owners manage everyone, admins manage admins and scorekeepers,
 * scorekeepers manage no one.
 */
export function canManageRole(
  actorRole: LeagueRole | undefined,
  role: LeagueRole,
): boolean {
  if (actorRole === "owner") return true;
  return actorRole === "admin" && role !== "owner";
}

/**
 * Whether a member with `role` may add, edit, or delete league data: league
 * settings, seasons, teams, players, rosters, and the game schedule. Mirrors
 * the staff checks in the API policies (which remain the authority).
 */
export function canManageLeague(role: LeagueRole | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * Whether a member with `role` may score games through Quick Score or the
 * Scoreboard: scores, game state, goals, penalties, and spares.
 *
 * It also covers the roster data scoring depends on — setting a player's jersey
 * and position, and creating a player for a spare who is not in the league yet.
 * Adding or removing roster entries stays with `canManageLeague`.
 */
export function canScoreGames(role: LeagueRole | undefined): boolean {
  return canManageLeague(role) || role === "scorekeeper";
}

/** Roles a member with `actorRole` may assign. */
export function assignableRoles(
  actorRole: LeagueRole | undefined,
): LeagueRole[] {
  return LEAGUE_ROLES.filter((role) => canManageRole(actorRole, role));
}
